import { Theme } from '@mui/material';

/**
 * Generates styles for a horizontally scrollable container with optional fading masks
 * @param {boolean} showLeftFade - Whether to show a fade effect on the left side.
 * @param {boolean} showRightFade - Whether to show a fade effect on the right side.
 * @returns {object} A style object to be applied to a scrollable container.
 */
export const fadeMaskStyles = (showLeftFade: boolean, showRightFade: boolean) => ({
  position: 'relative',
  display: 'flex',
  overflowX: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari
  },
  transition: 'mask-image 0.3s ease, -webkit-mask-image 0.3s ease',
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskImage:
    showLeftFade || showRightFade
      ? `linear-gradient(to right,
          ${showLeftFade ? 'transparent' : 'black'} 0px,
          black 30px,
          black calc(100% - 30px),
          ${showRightFade ? 'transparent' : 'black'} 100%)`
      : 'none',
  WebkitMaskImage:
    showLeftFade || showRightFade
      ? `linear-gradient(to right,
          ${showLeftFade ? 'transparent' : 'black'} 0px,
          black 30px,
          black calc(100% - 30px),
          ${showRightFade ? 'transparent' : 'black'} 100%)`
      : 'none',
});

const styles = {
  container: (responsive: boolean) => ({
    display: 'flex',
    backgroundColor: '#fff',
    padding: '0.75rem',
    borderRadius: '1rem',
    flexDirection: 'column',
    flexWrap: responsive ? 'wrap' : 'nowrap',
    alignContent: 'flex-start',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0px 4px 12px rgba(10, 16, 68, 0.08)',
  }),
  fullContainer: (responsive: boolean) => ({
    display: 'flex',
    flexDirection: responsive ? 'column' : 'row',
    flexWrap: responsive ? 'wrap' : 'nowrap',
    alignItems: 'center',
  }),
  leftContainer: {
    backgroundColor: '#fff',
    height: '66px',
    padding: 1,
    borderRadius: 1,
    mr: 2,
  },
  rightContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 1,
    padding: 1,
    overflow: 'hidden',
    height: '66px',
    justifyContent: 'space-between',
  },
  scrollContainer: {
    position: 'relative',
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  areasContainer: {
    position: 'relative',
    display: 'flex',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  addAreas: {
    flexShrink: 0,
    ml: 2,
  },
  button: {
    button: {
      padding: '0.5rem',
      fontSize: '14px',
      fontWeight: '400',
      color: 'gray.90',
      maxWidth: '200px',
      borderColor: ({ palette }: Theme) => palette.gray[90],
    },
    text: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    icon: (isSelected: boolean) => ({
      ...(isSelected && { color: ({ palette }: Theme) => palette.primary.main }),
    }),
  },
  containerResponsive: (responsive: boolean) => ({
    display: 'flex',
    flexDirection: responsive ? 'column' : 'row',
    gap: '0.5rem',
    flexWrap: responsive ? 'wrap' : 'nowrap',
    flex: 1,
    width: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  }),
  rightButton: { flexShrink: 0, display: 'flex', alignSelf: 'flex-start' },
  rightButtonBanner: {
    flexShrink: 0,
    display: 'flex',
    alignSelf: 'flex-start',
    lineHeight: 'normal',
    '.MuiButton-text': {
      pt: 0,
      pb: 0,
    },
  },
};

export default styles;
